/* eslint-disable no-underscore-dangle */
/* eslint-disable prefer-const */
import * as React from 'react';
import { navigate, Script } from 'gatsby';
import { StoreContext } from './store-context';
import Helpers from '../components/Product/Options/helpers';

const RISE_GIFT_CARD_HANDLE = 'rise-ai-giftcard';

export const ProductFormContext = React.createContext({});

export const ProductFormProvider = ({
	//	Data
	children,						//	Child components to render inside
	product,						//	(Object)		The Product Object used to initialize the product-form context.

	//	User Settings
	showErrors: defaultShowErrors,	//	*optional* (Bool):	Show 'hints' to user on Product Option selectors that need more selecting.

	//	Note: For renderingScheme & sortOrder, the keys defined going into the
	//  ContextProvider must be the slugified optionGroup.name string. Such as
	//  that returned by the Helpers.slugify() method.
	renderingScheme: defaultRenderingScheme = {},	//	*optional* (Object):	Allows control of how individual Product Option Groups should be rendered ('button','select','swatch'). Example: {0:'button',1:'select',2:'swatch'}
	sortOrder: defaultSortOrder = [],	//	*optional* (Array)	Allows control of the order of which the optionGroups are rearranged during render.

	//	User Settings: Variant Preselection
	preselectedVariant: defaultPreselectedVariant,						//	*optional* (Object):	The Variant object to pre-select Product Options for
	useUrlParameter,													//	*optional* (Bool):	Read & write variant.id changes to the browser's address bar.
	preselect1stAvailableVariant: defaultPreselect1stAvailableVariant,	//	*optional* (Bool):	Pre-select the 1st available Variant onload.
	usePrettyUrlParameters,												//	*optional* (Bool):	Instead of URL parameter of `variant=123456789102`, use `color=blue&size=large&weight=15-lb`

	//	User Settings: Variant Navigation
	useReplaceState,				//	*optional* (Bool):	When true, ProductOption selections that change the url will not create a history.
}) => {
	//	----------
	//	Dependency | Used to fetch product data
	//	----------
	const {
		client,
		addVariantsToCart,
		cartFlyoutOpen,
	} = React.useContext(StoreContext);
	const isBrowser = typeof window !== `undefined`;

	//	----------------------
	//	Pre-State Computations
	//		Variant Preselection
	//			in the following order from:
	//			-	URL get parameter
	//			-	The fact that there's only one of Product.variants; the 'default' variant
	//			-	The first 'available' variant
	//	----------------------
	const [preselect1stAvailableVariant, setPreselect1stAvailableVariant] = React.useState(defaultPreselect1stAvailableVariant);	//	eslint-disable-line max-len

	const preselectedVariant = () => {
		let urlVariant;

		if (usePrettyUrlParameters) {
			urlVariant = Helpers.getVariantFromUrlPrettyParameters(product);
		} else {
			urlVariant = Helpers.getVariantFromUrl(product);
		}

		let _preselectedVariant;
		switch (true) {
		case urlVariant != null:	//	Don't you dare tell me to use eqeqeq here.
			// console.log('Preselecting Variant from URL');		//	Debugging Only
			_preselectedVariant = urlVariant;
			break;
		case defaultPreselectedVariant != null:
			_preselectedVariant = defaultPreselectedVariant;
			break;
		case product.variants?.length === 1:
			//	Select the only variant that exists.
			// console.log('Preselecting default Variant');			//	Debugging Only
			[_preselectedVariant] = product.variants;
			break;
		default:
			//	Find first available variant & select it.
			// eslint-disable-next-line quotes
			// console.log("Preselecting first 'available' Variant");	//	Debugging Only
			if (preselect1stAvailableVariant) {
				const sortedVariants = product.variants.sort((a, b) => {
					if (a.position > b.position) return 1;
					if (a.position < b.position) return -1;
					return 0;
				})

				_preselectedVariant = sortedVariants.find(_variant => { return _variant.availableForSale; });

				// If there are no variants available for sale, just select the first one.
				if (!_preselectedVariant) {
					[_preselectedVariant] = sortedVariants
				}

				setPreselect1stAvailableVariant(false);	//	Consider moving this outside of switch(){} if more logic is added in the future
			}
		}// switch
		return _preselectedVariant;
	};

	//	Even though we set the default preselectedVariant into state, it does not update
	//	the browser's address bar here. That happens a handful of lines down from here.
	const [variant, _setVariant] = React.useState(preselectedVariant);

	//	------------
	//	Proxy Setter to perform URL updates before normal state setting behavior
	//	------------
	const setVariant = React.useCallback((_variant, replaceState) => {
		//	Update the URL to match selected Variant
		if (useUrlParameter) {
			// eslint-disable-next-line
			// if (typeof window !== 'undefined' && Helpers.getVariantFromUrl(product) != _variant) {
			if (typeof window !== 'undefined') {
				const url = new URL(window.location);
				if (!_variant?.storefrontId) {
					url.searchParams.delete('variant');
				} else {
					const variantAdminId = _variant.storefrontId.split('/').pop();
					url.searchParams.set('variant', variantAdminId);
				}

				//	Denested from above if(){} for readability
				if (usePrettyUrlParameters) {
					url.searchParams.delete('variant');
					_variant?.selectedOptions.forEach(selectedOption => {
						url.searchParams.set(
							Helpers.slugify(selectedOption.name),
							Helpers.slugify(selectedOption.value)
						);
					});
				}

				const params = url.searchParams.toString();
				const destinationUrl = [
					url.pathname,
					params ? '?' : '',
					params,
				].join('');

				if (useReplaceState && replaceState) {
					// Update the URL without creating a new entry in the browser's history.
					window.history.replaceState({}, '', destinationUrl);
				} else {
					// Update the URL and create a new entry in the browser's history.
					// Use `navigate` because we also want to trigger the `gatsby-route-change` event.
					navigate(destinationUrl, {
						replace: false,
						state: {
							disableScrollUpdate: true,
						}
					});
				}
			}
		}

		//	Invoke the Parent's setter function.
		_setVariant(_variant);
	}, [useUrlParameter, useReplaceState]); // eslint-disable-line

	//	We invoke the following to update the browser's address bar onload. It should realize
	//	the state object is already the same and exit after only the address change.
	// TENTATIVELY DEPRECATED
	// React.useEffect(() => {
	// 	if (preselectedVariant) {
	// 		setVariant(preselectedVariant);
	// 	}
	// }, [setVariant, preselectedVariant]);

	//	---------------------------------------------------------------
	//	Availability
	//	-	We don't want to statically render product options.
	//		This is because product availability changes gradually after
	//			the time our static build was generated.
	//		This makes the data used for the static rendering 'stale' and no-longer accurate.
	//		Thus, on every page-load, we need to fetch the most current real-time
	//			availability from Shopify before rendering the Product Options.
	//	---------------------------------------------------------------
	const [isLoading, setIsLoading] = React.useState(true);
	const [availabilityMap, setAvailabilityMap] = React.useState(null);	//	Default to null so we can easily detect when it's not initialized.

	React.useEffect(() => {
		client.Product.fetch(product.storefrontId || product.id).then(_p => {
			// console.log('Did fetch latest variant availability');
			const availMap = {};
			_p?.variants.forEach(_v => {
				availMap[btoa(_v.id)] = _v.available;
				return null;
			}); //  Load object with properties as ${id}:${available}
			setAvailabilityMap(availMap);
			setIsLoading(false);
		});
	}, [client.Product, product.storefrontId, product.id]);

	//	----------------------------
	//	Show Option Selection Errors
	//	----------------------------
	const [_showErrors, setShowErrors] = React.useState(defaultShowErrors);
	const [isRiseGiftCard, setIsRiseGiftCard] = React.useState(false);

	React.useEffect(() => {
		if (isBrowser) {
			const isRise = product.handle === RISE_GIFT_CARD_HANDLE;
			setIsRiseGiftCard(isRise);

			const handleGiftAdded = props => {
				const { gift } = props || {};
				const { _gift_id: giftId, message, email, name } = gift || {};
				const item = {
					quantity: 1,
					variantId: variant.storefrontId,
					attributes: [
						...(giftId ? [{ key: '_gift_id', value: giftId }] : []),
						...(name ? [{ key: 'Name', value: name }] : []),
						...(email ? [{ key: 'Email', value: email }] : []),
						...(message ? [{ key: 'Message', value: message }] : []),
					],
				};

				return addVariantsToCart([item])
					.then(cartFlyoutOpen)
					.catch(console.error);
			};

			window.Rise = isRise
				? {
						full_product: { available: true },
						is_floating_cart_theme: true,
						is_product_page: true,
						product: { id: product.storefrontId.split('/').pop() },
						using_add_to_cart_flow: false,
						onGiftAdded: handleGiftAdded,
					}
				: null;
		}
	}, [
		isBrowser,
		variant.storefrontId,
		product.handle,
		product.storefrontId,
		addVariantsToCart,
		cartFlyoutOpen
	])

	//	---------
	//	Helper(s)
	//	---------
	const isVariantAvailable = () => {
		if(availabilityMap === null) return undefined;
		return availabilityMap && !!availabilityMap[btoa(variant?.storefrontId)];
	};

	const isBundle = () => {
		if (product?.metafields?.find(field => {
			return field.key === 'bundle_collection'
			|| field.key === 'bundle_product_no_variant'
			|| field.key === 'bundle_product_with_variants'
			|| field.key === 'bundle_product_unique_variant';
		}) === undefined) {
			return false;
		}

		return true;
	};

	const riseScriptMarkup = isRiseGiftCard && (
			<Script
				src={`https://str.rise-ai.com/?shop=${process.env.GATSBY_SHOPIFY_PERMANENT_DOMAIN}`}
			/>
	);

	//	------
	//	Output
	//	------
	return (
		<ProductFormContext.Provider
			// eslint-disable-next-line react/jsx-no-constructed-context-values
			value={{
				//	Data
				product,
				variant,
				//	Ajax Data
				availabilityMap,
				//	Helpers
				isVariantAvailable,
				isBundle,
				isRiseGiftCard,
				//	State
				showErrors: _showErrors,
				//	State Setters
				setVariant,
				setShowErrors,
				// Status
				isLoading,
				//	User preferences
				renderingScheme: defaultRenderingScheme,
				sortOrder: defaultSortOrder,
			}}
		>
			{riseScriptMarkup}
			{children}
		</ProductFormContext.Provider>
	);
};
